import {
  backgroundConfigurationRequiredSelector,
  selectConfigurationShoot,
  selectConfigurationAdditionalData,
} from '../../../../../../selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openConfigSlot } from '../../../../../../reducers/productConfigSlice';
import useImageOrientation from '../../../../../../hooks/useImageOrientation';
import { isJpgImage } from '../../../../../../utils/imageHelpers';
import type { ImageIntentContext } from '../../../../../../utils/imageIntentReporter';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../../../../shared/protectedImage';
import { imageIntentGalleryOf } from '../../../../shared/protectedImage/imageIntentGallery';
import { productPosesFixed } from '../../../perPoseBackgrounds';
import { Box, BoxText } from './SelectedPosesAndBackgroundsStyles';

interface PoseSelection {
  url?: string;
  selected_crop?: { url: string };
}

interface BackgroundSelection {
  image_thumbnail?: string;
}

interface PoseSlotProps {
  index: number;
  showLabel?: boolean;
}

const PoseSlot = ({ index, showLabel = false }: PoseSlotProps) => {
  const dispatch = useDispatch();
  const isPrepay = useSelector(
    (state: any) => selectConfigurationShoot(state).isPrepay,
  );
  const poseSelections: PoseSelection[] = useSelector(
    (state: any) => state.productConfig.poseSelections,
  );
  const backgroundSelections: BackgroundSelection[] = useSelector(
    (state: any) => state.productConfig.backgroundSelections,
  );
  const posesFixed = useSelector((state: any) =>
    productPosesFixed(state.productConfig.currentProduct),
  );
  const backgroundStepAvailable = useSelector(
    backgroundConfigurationRequiredSelector,
  );
  const { isGeskus, picsGalleryId, studentPreviewImageUrl } = useSelector(
    (state: any) => selectConfigurationAdditionalData(state),
  );
  // Cmd/Ctrl+S saves the page, so it reports the order's gallery (the hook's
  // Redux defaults).
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'pose_slot',
  });

  const pose = poseSelections[index] ?? null;
  const poseImageUrl = pose?.selected_crop?.url ?? pose?.url ?? null;
  // The chosen photo reports its own gallery when it was pooled from another
  // one (a sibling's, or none for a group photo; see galleryOrderContext),
  // and the order's gallery otherwise. The throttle key follows the image on
  // screen, not the slot: a slot index is shared by every photo the parent
  // puts in that slot, so two different photos would share one throttle
  // bucket and the second attempt would be dropped.
  const frictionContext: ImageIntentContext = {
    surface: 'pose_slot',
    imageKey: poseImageUrl ?? studentPreviewImageUrl ?? `slot-${index}`,
    ...imageIntentGalleryOf(pose),
  };
  const urlsToCheck = [poseImageUrl, studentPreviewImageUrl].filter(
    Boolean,
  ) as string[];
  const { imageOrientations } = useImageOrientation(urlsToCheck) as {
    imageOrientations: Record<string, boolean>;
  };

  const background = backgroundSelections[index]
    ? `url(${backgroundSelections[index]?.image_thumbnail})`
    : '';

  // A fixed pose (a "background chosen per pose" product, perPoseBackgrounds)
  // cannot be swapped: tapping it chooses that pose's background instead.
  const handleEditPose = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (posesFixed) {
      if (backgroundStepAvailable) {
        dispatch(openConfigSlot({ slotIndex: index, step: 'background' }));
      }
      return;
    }
    dispatch(openConfigSlot({ slotIndex: index, step: 'pose' }));
  };

  if (poseSelections.length > 0 && pose) {
    const imageUrl = pose.selected_crop ? pose.selected_crop.url : pose.url!;
    const is_horizontal = imageOrientations[imageUrl] || false;
    const isJpg = isJpgImage(imageUrl);
    const boxBackground = isJpg ? '' : background;

    if (pose.selected_crop) {
      return (
        <Box
          background={boxBackground}
          is_horizontal={is_horizontal}
          onClick={handleEditPose}
        >
          <img
            {...getFrictionProps(frictionContext)}
            src={pose.selected_crop.url}
          />
          {showLabel && <BoxText>pose</BoxText>}
          <ImageProtectionOverlay
            context={frictionContext}
            imageSrc={pose.selected_crop.url}
          />
        </Box>
      );
    }

    return (
      <Box
        isShifted={index > 0}
        background={boxBackground}
        is_horizontal={is_horizontal}
        onClick={handleEditPose}
      >
        <img {...getFrictionProps(frictionContext)} src={pose.url} />
        {showLabel && <BoxText>pose</BoxText>}
        <ImageProtectionOverlay context={frictionContext} imageSrc={pose.url} />
      </Box>
    );
  }

  if (isPrepay) {
    return null;
  }

  // Note: studentPreviewImageUrl should only be available for Geskus New England; we
  // don't want to display it for a PicsGallery-driven order
  if (studentPreviewImageUrl && !picsGalleryId && isGeskus) {
    const preview_is_horizontal =
      imageOrientations[studentPreviewImageUrl] || false;
    const isJpg = isJpgImage(studentPreviewImageUrl);
    const boxBackground = isJpg ? '' : background;

    return (
      <Box
        isShifted={index > 0}
        background={boxBackground}
        is_horizontal={preview_is_horizontal}
        onClick={handleEditPose}
      >
        <img
          {...getFrictionProps(frictionContext)}
          src={studentPreviewImageUrl}
        />
        {showLabel && <BoxText>pose</BoxText>}
        <ImageProtectionOverlay
          context={frictionContext}
          imageSrc={studentPreviewImageUrl}
        />
      </Box>
    );
  }

  return (
    <Box isShifted={index > 0} is_horizontal={false} onClick={handleEditPose}>
      {showLabel && <BoxText>pose</BoxText>}
    </Box>
  );
};

export default PoseSlot;
